'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PopupShell,
  type PopupLayout,
  type PopupCorner,
  type PopupAnimation,
} from './PopupShell'
import { PopupContent, type MediaPosition } from './PopupContent'
import {
  resolvePopupStyle,
  type PopupStyleMode,
  type PopupStylePreset,
  type CustomPopupStyle,
} from './popup-style'

export type PopupTriggerMode = 'auto' | 'click'

export type PopupItem = {
  id: string
  // Content
  headline: string | null
  body: string | null
  cta_label: string | null
  cta_href: string | null
  // Secondary CTA — when label is set and href is null, clicking dismisses
  // (the classic "No thanks" inline link). When both set, it's a normal link.
  secondary_cta_label: string | null
  secondary_cta_href: string | null
  image_url: string | null
  image_alt: string | null
  // Image placement within the popup body. Only meaningful for modal /
  // takeover layouts; slide_in and bottom_bar ignore it.
  media_position: MediaPosition
  // Layout
  layout: PopupLayout
  // Corner position for the slide_in layout (expanded to four corners).
  // null falls back to bottom_right at render time.
  slide_in_corner: PopupCorner | null
  backdrop_dim: boolean
  dismissible: boolean
  // Entrance animation. Defaults to layout-appropriate motion when 'none'
  // is not chosen; respects prefers-reduced-motion at the host.
  animation: PopupAnimation
  // Style
  style_mode: PopupStyleMode
  style_preset: PopupStylePreset | null
  custom_styles: CustomPopupStyle | null
  // Triggers
  trigger_mode: PopupTriggerMode
  trigger_slug: string | null
  trigger_page_load_delay_seconds: number | null
  trigger_scroll_depth_percent: number | null
  trigger_exit_intent: boolean
  trigger_time_on_site_seconds: number | null
  // Targeting/sort
  priority: number
  // Frequency
  cooldown_days: number
  max_impressions_per_visitor: number | null
}

export type PopupHostProps = {
  items: PopupItem[]
  /** Optional analytics callback. Called at impression, dismiss, and CTA click.
   *  Storefront passes `(type, meta) => track(type, meta)`.
   *  Portal callers omit this prop — no analytics side-effects. */
  onEvent?: (type: string, meta?: Record<string, string>) => void
}

const TIME_ON_SITE_KEY = 'sifly-time-on-site-ms'
const lastShownKey = (id: string) => `sifly-popup-${id}-last-shown`
const impressionsKey = (id: string) => `sifly-popup-${id}-impressions`

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isEligibleForFrequency(item: PopupItem): boolean {
  if (typeof localStorage === 'undefined') return true
  // Max impressions cap.
  if (item.max_impressions_per_visitor !== null) {
    const raw = localStorage.getItem(impressionsKey(item.id))
    const n = raw ? parseInt(raw, 10) : 0
    if (Number.isFinite(n) && n >= item.max_impressions_per_visitor) return false
  }
  // Cooldown.
  if (item.cooldown_days > 0) {
    const raw = localStorage.getItem(lastShownKey(item.id))
    if (raw) {
      const last = parseInt(raw, 10)
      if (Number.isFinite(last)) {
        const elapsedMs = Date.now() - last
        const cooldownMs = item.cooldown_days * 24 * 60 * 60 * 1000
        if (elapsedMs < cooldownMs) return false
      }
    }
  }
  return true
}

function recordImpression(item: PopupItem): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(lastShownKey(item.id), String(Date.now()))
  const raw = localStorage.getItem(impressionsKey(item.id))
  const n = raw ? parseInt(raw, 10) : 0
  localStorage.setItem(impressionsKey(item.id), String((Number.isFinite(n) ? n : 0) + 1))
}

export function PopupHost({ items, onEvent }: PopupHostProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const firedThisPageView = useRef<Set<string>>(new Set())
  const reducedMotion = useRef(false)

  useEffect(() => {
    setHydrated(true)
    reducedMotion.current = prefersReducedMotion()
  }, [])

  const fire = useCallback(
    (item: PopupItem) => {
      if (firedThisPageView.current.has(item.id)) return
      if (!isEligibleForFrequency(item)) return
      if (activeId) return
      firedThisPageView.current.add(item.id)
      recordImpression(item)
      setActiveId(item.id)
      const meta: Record<string, string> = { popup_id: item.id }
      if (item.trigger_mode) meta.trigger_kind = item.trigger_mode
      onEvent?.('popup_impression', meta)
    },
    [activeId, onEvent],
  )

  const dismiss = useCallback(() => {
    setActiveId((prev) => {
      if (prev) onEvent?.('popup_dismiss', { popup_id: prev, via: 'dismiss' })
      return null
    })
  }, [onEvent])

  // Partition + sort by priority desc, then id.
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority
      return a.id.localeCompare(b.id)
    })
  }, [items])

  const autoItems = useMemo(() => sorted.filter((i) => i.trigger_mode === 'auto'), [sorted])
  const clickItems = useMemo(() => sorted.filter((i) => i.trigger_mode === 'click'), [sorted])

  // Page-load delay triggers.
  useEffect(() => {
    if (!hydrated) return
    const timeouts: number[] = []
    for (const item of autoItems) {
      const d = item.trigger_page_load_delay_seconds
      if (d === null || d < 0) continue
      const ms = Math.max(0, d * 1000)
      const t = window.setTimeout(() => fire(item), ms)
      timeouts.push(t)
    }
    return () => timeouts.forEach((t) => window.clearTimeout(t))
  }, [hydrated, autoItems, fire])

  // Scroll-depth triggers.
  useEffect(() => {
    if (!hydrated) return
    const candidates = autoItems.filter(
      (i) => i.trigger_scroll_depth_percent !== null && i.trigger_scroll_depth_percent > 0,
    )
    if (candidates.length === 0) return
    let ticking = false
    function onScroll() {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        const doc = document.documentElement
        const scrolled = window.scrollY + window.innerHeight
        const total = doc.scrollHeight
        const pct = total > 0 ? (scrolled / total) * 100 : 0
        for (const item of candidates) {
          if (pct >= (item.trigger_scroll_depth_percent ?? 100)) {
            fire(item)
          }
        }
        ticking = false
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    // Fire once on mount in case page already scrolled enough.
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [hydrated, autoItems, fire])

  // Exit-intent triggers (desktop only).
  useEffect(() => {
    if (!hydrated) return
    const candidates = autoItems.filter((i) => i.trigger_exit_intent)
    if (candidates.length === 0) return
    if (window.innerWidth < 1024) return
    let armed = true
    function onMouseLeave(e: MouseEvent) {
      if (!armed) return
      if (e.clientY > 0) return
      armed = false
      for (const item of candidates) fire(item)
    }
    document.documentElement.addEventListener('mouseleave', onMouseLeave)
    return () => document.documentElement.removeEventListener('mouseleave', onMouseLeave)
  }, [hydrated, autoItems, fire])

  // Time-on-site triggers (session-cumulative across pages).
  useEffect(() => {
    if (!hydrated) return
    const candidates = autoItems.filter(
      (i) => i.trigger_time_on_site_seconds !== null && i.trigger_time_on_site_seconds > 0,
    )
    if (candidates.length === 0) return
    let interval: number | null = null
    function tick() {
      if (document.visibilityState !== 'visible') return
      const raw =
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(TIME_ON_SITE_KEY) : null
      const prev = raw ? parseInt(raw, 10) : 0
      const next = (Number.isFinite(prev) ? prev : 0) + 1000
      try {
        sessionStorage.setItem(TIME_ON_SITE_KEY, String(next))
      } catch {
        // private mode etc — ignore
      }
      const seconds = Math.floor(next / 1000)
      for (const item of candidates) {
        if (seconds >= (item.trigger_time_on_site_seconds ?? Infinity)) fire(item)
      }
    }
    interval = window.setInterval(tick, 1000)
    return () => {
      if (interval !== null) window.clearInterval(interval)
    }
  }, [hydrated, autoItems, fire])

  // Click triggers — delegated document listener.
  useEffect(() => {
    if (!hydrated) return
    if (clickItems.length === 0) return
    const bySlug = new Map<string, PopupItem>()
    for (const item of clickItems) {
      if (item.trigger_slug) bySlug.set(item.trigger_slug, item)
    }
    if (bySlug.size === 0) return
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null
      if (!target) return
      const el = target.closest<HTMLElement>('[data-sifly-popup]')
      if (!el) return
      const slug = el.getAttribute('data-sifly-popup')
      if (!slug) return
      const item = bySlug.get(slug)
      if (!item) return
      // Click triggers ignore the page-view-fired suppression so a button
      // can re-open after dismiss (subject to frequency cap).
      firedThisPageView.current.delete(item.id)
      if (el.tagName === 'A') e.preventDefault()
      fire(item)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [hydrated, clickItems, fire])

  // Wrap dismiss for CTA clicks: emit popup_cta_click before closing.
  const handleCtaClick = useCallback((href: string | null) => {
    if (activeId) {
      const meta: Record<string, string> = { popup_id: activeId }
      if (href) meta.href = href
      onEvent?.('popup_cta_click', meta)
    }
    dismiss()
  }, [activeId, onEvent, dismiss])

  if (!hydrated) return null
  if (!activeId) return null
  const active = sorted.find((i) => i.id === activeId)
  if (!active) return null

  const resolved = resolvePopupStyle({
    style_mode: active.style_mode,
    style_preset: active.style_preset,
    custom_styles: active.custom_styles,
  })

  const compact = active.layout === 'bottom_bar'
  const supportsMediaPosition = active.layout === 'modal' || active.layout === 'takeover'
  const mediaPosition: MediaPosition = supportsMediaPosition ? active.media_position : 'top'

  // reducedMotion is a mount-constant ref (set once from matchMedia); reading
  // .current in render is intentionally non-reactive.
  // eslint-disable-next-line react-hooks/refs
  const reducedMotionValue = reducedMotion.current

  return (
    <PopupShell
      layout={active.layout}
      slideInCorner={active.slide_in_corner ?? 'bottom_right'}
      resolvedStyle={resolved}
      backdropDim={active.backdrop_dim}
      dismissible={active.dismissible}
      animation={active.animation}
      onDismiss={dismiss}
      reducedMotion={reducedMotionValue}
      hasBackgroundMedia={mediaPosition === 'background' && !!active.image_url}
      backgroundMediaUrl={mediaPosition === 'background' ? active.image_url : null}
    >
      <PopupContent
        headline={active.headline}
        body={active.body}
        ctaLabel={active.cta_label}
        ctaHref={active.cta_href}
        secondaryCtaLabel={active.secondary_cta_label}
        secondaryCtaHref={active.secondary_cta_href}
        imageUrl={active.image_url}
        imageAlt={active.image_alt}
        mediaPosition={mediaPosition}
        compact={compact}
        onCtaClick={() => handleCtaClick(active.cta_href)}
        onSecondaryClick={dismiss}
      />
    </PopupShell>
  )
}
